import React, { FC } from 'react'
import { getRgbFromHex, isColorDark } from 'shared/colorUtils'
import SVG from 'react-inlinesvg'
import { useWindowSize } from 'the-platform'

import { BREAK_MOBILE } from '../../utils/breakpoints'
import { Dropdown } from '../common/dropdown/Dropdown'
import { UserIcon } from '../common/user/user-icon/UserIcon'
import { AccountDropdown } from './AccountDropdown'
import { HeaderSearch } from './HeaderSearch'
import { Navigation } from '../navigation/Navigation'
import { UserDropdown } from './UserDropdown'
import { useRouteMatch } from 'react-router-dom'
import { staticLinks } from 'shared/linkBuilder'
import IconArrowLeft from '../common/icon/IconArrowLeft'
import { useIntercom } from '../../public-api/hooks/Intercom/useIntercom'
import { apolloClientPublic } from '../../graphql/apolloClient'
import { css } from '@emotion/react'
import { useWorkspaceOnlyNameQuery } from 'front-end/src/pages/manage-workspace-page/ManageWorkspacePage.codegen'
import { Box, Flex } from '@chakra-ui/react'
import ReactGA from 'react-ga'
import environment from 'shared/environment'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'
import { getOrganisationLogoSrc } from 'shared/getImageVariant'
import { navigateTo } from 'front-end/src/app/util/appHistory'
import { t, Trans } from '@lingui/macro'
import { brandColors } from 'shared/basicPalette'
import { MainHeaderQuery, useMainHeaderQuery } from './MainHeader.codegen'
import { LinkOverlay } from 'front-end/src/components'
import NavbarChevronDown from 'public/assets/img/navbar-chevron-down.svg'
import './MainHeader.less'
import { useLooopTheme } from '../../public-api/hooks/useLooopTheme'
import { AdminOnlyAllowed } from '../common/AdminOnlyAllowed'

export const useMainHeaderOrgQuery = () => {
  const { data } = useMainHeaderQuery({
    fetchPolicy: 'cache-first',
    client: apolloClientPublic
  })

  return data?.organisation
}

const MainHeaderOrgLogo: FC = () => {
  const organisation = useMainHeaderOrgQuery()
  if (!organisation) {
    return null
  }

  const logo = getOrganisationLogoSrc(organisation)
  return (
    <LinkOverlay className="MainHeader__logo" to="/" tabIndex={0}>
      {logo && (
        <img
          src={logo}
          alt={organisation.name as string}
          className="MainHeader__logo__image"
        />
      )}
      {!logo && organisation.name && <span>{organisation.name}</span>}
    </LinkOverlay>
  )
}

const MainHeaderWorkspaceLogo: FC<{
  workspaceId: number
}> = ({ workspaceId }) => {
  const { data, loading } = useWorkspaceOnlyNameQuery({
    variables: { workspaceId }
  })

  if (loading) {
    return null
  }
  return <Box className={'FixedHeader__workspace'}>{data?.workspace?.name}</Box>
}

const HeaderWrapper: FC<{
  organisation: NonNullable<MainHeaderQuery['organisation']>
}> = (props) => {
  const { children } = props
  const { theme } = useLooopTheme()
  const searchColorHex = isColorDark(theme.colors.navbarBackground)
    ? brandColors.white
    : brandColors.grey.dark
  const searchColorRgb = { ...getRgbFromHex(searchColorHex) }
  const textColorHex = theme.colors.navbarLink
  return (
    <header
      className="MainHeader"
      css={css`
        background-color: ${theme.colors.navbarBackground};
        transition: top 0.75s;
        .Dropdown .Dropdown__content:before {
          left: 80%;
        }
        .Navigation__menuToggle,
        .MainHeader__account__label,
        .MainHeader__logo,
        .MainHeader__search__toggle,
        .FixedHeader__back,
        .FixedHeader__workspace,
        .MainHeader__search__form input,
        .MainHeader__search__form .icon-magnifier {
          color: ${textColorHex};
        }
        .MainHeader__search__form {
          .Dropdown__handler .icon {
            color: ${searchColorHex};
          }
          input {
            color: ${searchColorHex};
            background-color: rgba(
              ${searchColorRgb.r},
              ${searchColorRgb.g},
              ${searchColorRgb.b},
              0.15
            );
            &::placeholder {
              color: rgba(
                ${searchColorRgb.r},
                ${searchColorRgb.g},
                ${searchColorRgb.b},
                0.4
              );
            }
            &:active,
            &:focus {
              background-color: rgba(
                ${searchColorRgb.r},
                ${searchColorRgb.g},
                ${searchColorRgb.b},
                0.2
              );
            }
          }
          .icon-magnifier {
            opacity: 0.6;
          }
        }
        .Dropdown #navbar-chevron-down {
          stroke: ${textColorHex};
        }
      `}
    >
      {children}
    </header>
  )
}

export const MainHeader: FC = () => {
  const { session } = usePublicApiSession()
  const isLoggedIn = session
  const { width } = useWindowSize()
  const organisation = useMainHeaderOrgQuery()

  useIntercom(width < BREAK_MOBILE)
  const workspaceIdMatch = useRouteMatch<{ workspaceId: string }>(
    '/workspace/:workspaceId'
  )

  if (!organisation) {
    return null
  }

  const user = session?.user
  if (!user) {
    return null
  }
  if (environment.isProduction()) {
    ReactGA.set({ userId: user.id })
    ReactGA.set({ dimension1: user.organisation_role }) // This dimension is linked to userType
  }
  return (
    <HeaderWrapper organisation={organisation}>
      <>
        {isLoggedIn && !(workspaceIdMatch && width < BREAK_MOBILE) && (
          <Navigation />
        )}
        {workspaceIdMatch && width < BREAK_MOBILE && (
          <span className="FixedHeader__back">
            <IconArrowLeft
              onClick={() => {
                navigateTo(staticLinks.home)
              }}
            />
          </span>
        )}
        {workspaceIdMatch && width < 540 ? ( // any screen smaller than @break-mobile
          <MainHeaderWorkspaceLogo
            workspaceId={Number(workspaceIdMatch.params.workspaceId)}
          />
        ) : (
          <MainHeaderOrgLogo />
        )}
        <HeaderSearch />
        <Flex display={['none', 'inherit']}>
          <AdminOnlyAllowed>
            <Box
              className="MainHeader__account"
              css={css`
                .Dropdown .Dropdown__content {
                  right: auto;
                  transform: translateY(12px) translateX(-70%);
                }
              `}
            >
              <Dropdown h="64px" p="6px" content={<AccountDropdown />}>
                <Flex
                  alignItems="center"
                  h="100%"
                  className="MainHeader__account__label"
                >
                  <Trans>Account</Trans>{' '}
                  <Box ml="8px">
                    <SVG aria-label={t`Down`} src={NavbarChevronDown} />
                  </Box>
                </Flex>
              </Dropdown>
            </Box>
          </AdminOnlyAllowed>
          <Box className="MainHeader__user" pr={[0, '12px', '16px']}>
            <Dropdown h="64px" p="6px" content={<UserDropdown />}>
              <Flex
                alignItems="center"
                h="100%"
                css={css`
                  &:hover svg {
                    opacity: 0.66;
                  }
                `}
              >
                <UserIcon user={user} size={40} />
                <Box>
                  <SVG aria-label={t`Down`} src={NavbarChevronDown} />
                </Box>
              </Flex>
            </Dropdown>
          </Box>
        </Flex>
      </>
    </HeaderWrapper>
  )
}

export const MainHeaderPublic: React.FC<{}> = () => {
  const organisation = useMainHeaderOrgQuery()

  if (!organisation) {
    return null
  }

  return (
    <HeaderWrapper organisation={organisation}>
      <MainHeaderOrgLogo />
    </HeaderWrapper>
  )
}
